/* eslint-disable @typescript-eslint/no-extraneous-class */
import { AxiosResponse } from 'axios';

type UnitOfTime = 'minutes' | 'hours' | 'days';

interface LocalStorageAxiosResponse<T> {
  response: AxiosResponse<T>;
  expiresOn: Date;
}

export class LocalStorageHelper {
  static getLocalResponseDataWithExpiration<T>(localStorageKey: string): AxiosResponse<T> | null {
    try {
      const localData = localStorage.getItem(localStorageKey);

      if (localData === null) {
        return null;
      }

      const localResponse = JSON.parse(localData) as LocalStorageAxiosResponse<T>;

      return new Date().getTime() > new Date(localResponse.expiresOn).getTime() ? null : localResponse.response;
    } catch {
      localStorage.removeItem(localStorageKey);
      return null;
    }
  }

  static setLocalResponseDataWithExpiration<T>(
    localStorageKey: string,
    response: AxiosResponse<T>,
    cacheValue: number = 1,
    cacheUnitOfTime: UnitOfTime = 'days'
  ): void {
    const expiresOn = LocalStorageHelper.addTime(new Date(), cacheValue, cacheUnitOfTime);
    localStorage.setItem(localStorageKey, JSON.stringify({ response, expiresOn }));
  }

  static getLocalResponseData<T>(localStorageKey: string): AxiosResponse<T> | null {
    try {
      const localData = localStorage.getItem(localStorageKey);

      if (localData === null) {
        return null;
      }

      const localResponse = JSON.parse(localData) as AxiosResponse<T>;

      return localResponse;
    } catch {
      localStorage.removeItem(localStorageKey)
      return null;
    }
  }

  static setLocalResponseData<T>(localStorageKey: string, response: AxiosResponse<T>): void {
    localStorage.setItem(localStorageKey, JSON.stringify(response));
  }

  private static addTime(date: Date, value: number, unit: UnitOfTime): Date {
    return new Date(date.getTime() + LocalStorageHelper.convertToMillis(value, unit));
  }

  private static convertToMillis(value: number, unit: UnitOfTime): number {
    switch (unit) {
      case 'minutes': {
        return value * 60 * 1000;
      }
      case 'hours': {
        return value * 60 * 60 * 1000;
      }
      case 'days': {
        return value * 24 * 60 * 60 * 1000;
      }
      default:
        return 0;
    }
  }
}
